import React, { useState, useEffect, useRef } from "react";
import { Button, Container, Row, Col } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import ReactPlayer from "react-player";
import { UserInfo } from "../helper/Context";
import LoginModal from "../components/LoginModal";
import WorkflowAnimation from "../components/WorkflowAnimation";
import "./NewHomePage.css"; // Import the new stylesheet

const useCases = [
  {
    title: "Explainer videos generated with a prompt",
    description: "Transform complex biomedical concepts into engaging video explainers with AI-powered narration and interactive 3D visualization. Simply describe what you want to explain, and Snorkle generates a complete video with professional voiceover in multiple languages, customizable visuals, and scientifically accurate content. No video editing required.",
    videoUrl: "https://vimeo.com/1138516050",
    thumbnail: "/snorkle_vis_thumbnails/gen_tour_thumb.jpg",
    link: "/explainer",
    contactLink: true
},
{
  title: "Science and medicine, in any language",
  description: "Break language barriers with visual communication. Whether you're creating patient education videos or annotating molecular structures for classes, Snorkle enables automatic translation and text-to-speech voiceover for over 70 different languages.",
  videoUrl: "https://vimeo.com/1067878765",
  thumbnail: "/snorkle_vis_thumbnails/multi_device_sync_thumb.jpg",
  contactLink: true
},
  {
        title: "Create interactive presentation experiences",
        description: "Convert traditional slide decks into interactive, mobile-activating experiences using either Snorkle's slides function or through a free add-in for Microsoft PowerPoint. For each visual created, Snorkle automatically generates QR codes that link to mobile-friendly, interactive visuals that give attendees the same amount of control and inspection capabilities as the presenter.",
        videoUrl: "https://vimeo.com/1067867379",
        thumbnail: "/snorkle_vis_thumbnails/slide_deck_thumb.jpg",
        link: "/powerpoint",
        contactLink: true
    },
    {
        title: "From deep learning to patient understanding",
        description: "Transform complex clinical imaging into patient-friendly explanations. Automatically segment DICOM studies, generate interactive 3D renderings, and automatically create clear, understandable videos all in one platform",
        videoUrl: "https://vimeo.com/1138551349",
        thumbnail: "/snorkle_vis_thumbnails/organ_segmentation_thumb.jpg",
        contactLink: true
    },
    {
        title: "Seamlessly share your research",
        description: "Obtain citable DOIs with Snorkle (through Crossref), making it simple to add interactive visuals to your publication list. Whether you're analyzing two-photon microscopy stacks, molecular structures, or medical imagery, focus on discovery, not file compatibility.",
        videoUrl: "https://vimeo.com/1067891367",
        thumbnail: "/snorkle_vis_thumbnails/confocal_microscopy_thumb.jpg",
        contactLink: true
    },
    // {
    //     title: "Make your social media interactive",
    //     description: "Bring your science to life on social media. Turn static posts into interactive experiences by embedding volumetric renderings, confocal image stacks, and 3D models directly into your Twitter, LinkedIn, and other platforms. Engage your audience with science they can explore, rotate, and zoom—right from their feed.",
    //     imageUrl: "https://snorkle-output-files.s3.amazonaws.com/tweet_image.png",
    //     contactLink: true
    // },
    // {
    //     title: "Narrate Your Data, Live",
    //     description: "Bring your data to life with live narration and screen recording. Whether you're a radiologist explaining an MRI, a researcher presenting findings, or an educator guiding students, our platform lets you talk through complex visuals in real time. Capture and share interactive sessions with picture-in-picture video, so your audience can see both you and the data. Perfect for remote collaboration, teaching, and scientific storytelling—seamlessly communicate insights with clarity and impact.",
    //     imageUrl: "https://snorkle-visuals.s3.us-east-1.amazonaws.com/screen-recording1.png",
    //     beta: true
    // },
    {
        title: "Build Your Own Virtual Museum",
        description: "With our powerful scientific visualization tools, you can annotate, markup, and transform your 3D fossil scans or downloads from Morphosource and other paleontology repositories into interactive 3D experiences—perfect for kiosks, touchscreen displays, and online galleries. You can even add voiceovers! Engage audiences with dynamic storytelling, allowing them to explore complex concepts with ease. No coding required—just upload, enhance, and showcase.",
        videoUrl: "https://vimeo.com/1067900567",
        thumbnail: "/snorkle_vis_thumbnails/museum_thumb_image.jpg",
        contactLink: true
    },
    // {
    //     title: "Become a Molecular Storyteller",
    //     description: "Become a molecular storyteller. Unlock the power of interactive protein visualizations to bring your science outreach to life. Highlight individual residues, track their position in the sequence, and narrate the story behind every molecular interaction. Create engaging, educational content that connects the dots between structure and function in a way that's easy to understand and visually captivating.",
    //     videoUrl: "https://vimeo.com/1068169832",
    //     beta: true
    // },
    {
        title: "Engage visitors on your website",
        description: "Offer engaging, interactive visuals on your group's website. Each Snorkle visual can be embedded simply by copying and pasting html code snippets.",
        imageUrl: "https://snorkle-output-files.s3.amazonaws.com/lab_webpage_embed-use-case-2monitor.png",
        contactLink: true,
        hasWhitePanel: true
    },
    {
        title: "Deliver Verified Visuals",
        description: "Whether it's for the purpose of giving your practice's waiting room experience a 3D makeover, supplementing teaching, or creating marketing materials, build amazing, accurate scientific experiences.",
        images: [
            "https://snorkle-output-files.s3.amazonaws.com/mobiledisplays_usecase_all.png",
            "https://snorkle-visuals.s3.us-east-1.amazonaws.com/waiting-room-menu.png"
        ],
        contactLink: true,
        hasWhitePanel: true
    }
];

const HomePage = () => {
  const navigate = useNavigate();
  const starCanvasRef = useRef(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [heroVideoReady, setHeroVideoReady] = useState(false);
  const [videosReady, setVideosReady] = useState({});

  const handleVideoReady = (videoUrl) => {
    setVideosReady(prev => ({ ...prev, [videoUrl]: true }));
  };

  // Helper function to render title with rotating language effect
  const renderTitle = (title) => {
    if (title === "Science and medicine, in any language") {
      return (
        <h2>
          Science and medicine, in any{" "}
          <span className="rotating-language-container">
            <span className="rotating-language">
              <span className="language-word lang-english">language</span>
              <span className="language-word lang-arabic">لغة</span>
              <span className="language-word lang-japanese">言語</span>
              <span className="language-word lang-hindi">भाषा</span>
              <span className="language-word lang-hebrew">שפה</span>
            </span>
          </span>
        </h2>
      );
    }
    return <h2>{title}</h2>;
  };

  useEffect(() => {
    // Canvas animation effect from original page
    const canvas = starCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let stars = [];
    const handleResize = () => {
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const intendedDrawBufferWidth = window.innerWidth;
      const intendedDrawBufferHeight = window.innerHeight;
      canvas.width = intendedDrawBufferWidth * dpr;
      canvas.height = intendedDrawBufferHeight * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0); 
      ctx.scale(dpr, dpr);
      stars = [];
      for (let i = 0; i < 200; i++) {
        stars.push({
          x: Math.random() * intendedDrawBufferWidth,
          y: Math.random() * intendedDrawBufferHeight,
          r: Math.random() * 1.5,
          dx: (Math.random() - 0.5) * 0.3,
          dy: (Math.random() - 0.5) * 0.3
        });
      }
    };
    let animationFrameId;
    function draw() {
      if (!ctx || !canvas) return;
      ctx.save();
      const currentDrawWidth = window.innerWidth;
      const currentDrawHeight = window.innerHeight;
      ctx.clearRect(0, 0, currentDrawWidth, currentDrawHeight);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      stars.forEach(s => {
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, 2 * Math.PI);
        ctx.fill();
        s.x += s.dx;
        s.y += s.dy;
        if (s.x < 0) { s.x = currentDrawWidth + s.r; } 
        else if (s.x > currentDrawWidth + s.r) { s.x = -s.r; }
        if (s.y < 0) { s.y = currentDrawHeight + s.r; } 
        else if (s.y > currentDrawHeight + s.r) { s.y = -s.r; }
      });
      ctx.restore();
      animationFrameId = requestAnimationFrame(draw);
    }
    const initialResizeTimeoutId = setTimeout(() => {
      handleResize();
      draw();
    }, 0);
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(initialResizeTimeoutId);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <>
      <div 
        className="homepage-container"
        style={{
          width: '100%',
          position: 'relative',
          overflowX: 'hidden',
          background: 'linear-gradient(to bottom, #0a0f2c, #1b1f3a, #2d304d)',
        }}
      >
        <canvas 
          id="stars" 
          ref={starCanvasRef} 
          style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}
        ></canvas>
        <div 
          className="overlay" 
          style={{ 
            position: 'fixed', 
            top: 0, left: 0, right: 0, bottom: 0, 
            background: 'radial-gradient(circle at center, rgba(255,255,255,0.03), rgba(0,0,0,0.9))', 
            pointerEvents: 'none', 
            zIndex: 1
          }}
        ></div>

        <div style={{ position: 'relative', zIndex: 2 }}>
          <section className="scroll-section hero-section">
            <Container>
              <Row className="align-items-center">
                <Col lg={6} md={12} className="hero-text-col">
                  <h1>Where Science Becomes Understanding.</h1>
                  <p className="lead">
                  Turn complex science into clear, compelling communication—and see how well people actually understand it. Build interactive decks, articles, and learning materials. Generate verified visuals. Snorkle helps create public-ready media and quantify impact.
                  </p>
                  <UserInfo.Consumer>
                    {({ userInfo }) =>
                      userInfo ? (
                        <Button
                          variant="primary"
                          size="lg"
                          onClick={() => navigate("/dashboard", { state: { openWizard: true } })}
                          className="cta-button"
                        >
                          Start Creating
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="lg"
                          onClick={() => {
                            setShowLoginModal(true);
                          }}
                          className="cta-button"
                        >
                    Start Creating
                  </Button>
                      )
                    }
                  </UserInfo.Consumer>
                </Col>
                <Col lg={6} md={12} className="hero-video-col">
                  <div className="phone-mockup" style={{ position: 'relative' }}>
                    {!heroVideoReady && (
                      <img 
                        src="/snorkle_vis_thumbnails/scrollytelling_thumb.jpg"
                        alt="Loading video..."
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          zIndex: 1,
                          borderRadius: 'inherit'
                        }}
                      />
                    )}
                    <ReactPlayer
                      url="https://vimeo.com/1091699735"
                      playing
                      loop
                      muted
                      playsInline
                      controls={false}
                      width="100%"
                      height="100%"
                      onStart={() => setHeroVideoReady(true)}
                      config={{
                        vimeo: {
                          playerOptions: {
                            autoplay: true,
                            muted: true,
                            playsinline: true,
                            background: true
                          }
                        }
                      }}
                    />
                  </div>
                </Col>
              </Row>
            </Container>
          </section>

          <section className="scroll-section nih-section">
            <div className="content-box">
              <h2>Trust at the Highest Levels</h2>
              <p>
              Snorkle Labs is supported by the National Institutes of Health (NIH) / National Institute of General Medical Sciences (NIGMS) and provides citable DOIs through Crossref. Built on a rigorous, human-centered foundation, Snorkle enables scientifically accurate, trustworthy communication—from research to real-world impact.
              </p>
              <div className="logo-container">
                <img src="https://upload.wikimedia.org/wikipedia/commons/c/c8/NIH_Master_Logo_Vertical_2Color.png" alt="NIH Logo" className="nih-logo" />
                <img src="https://assets.crossref.org/logo/crossref-logo-200.svg" alt="Crossref Logo" className="crossref-logo" />
              </div>
            </div>
          </section>

          {useCases.filter(useCase => useCase.videoUrl || useCase.imageUrl || (useCase.images && useCase.images.length > 0)).map((useCase, index) => (
            <React.Fragment key={index}>
              {index === 2 && (
                <div className="use-case-panel">
                  <section className="scroll-section use-case-scroll-section">
                    <Container className="scrolly-container">
                      <div className="scrolly-text-content">
                        <div className="scrolly-text-block">
                          <h2>From discovery to impact</h2>
                          <p>
                            Built for the modern scientific ecosystem, Snorkle empowers researchers and communicators across the entire lifecycle of discovery. 
                            Leverage agentic science tools, commmunicate findings in professional settings, 
                            obtain citable DOIs for interactive visuals, and create engaging public-facing media.
                          </p>
                        </div>
                      </div>
                      <div className="sticky-visual">
                        <WorkflowAnimation />
                      </div>
                    </Container>
                  </section>
                </div>
              )}
            <div className="use-case-panel">
              <section className="scroll-section use-case-scroll-section">
                  <Container className="scrolly-container">
                      <div className="scrolly-text-content">
                          <div className="scrolly-text-block">
                              {renderTitle(useCase.title)}
                              <p>{useCase.description}</p>
                              <div className="use-case-actions">
                                  <UserInfo.Consumer>
                                    {({ userInfo }) => (
                                      <button
                                        className="use-case-button"
                                        onClick={() => {
                                          if (userInfo) {
                                            navigate("/dashboard");
                                          } else {
                                            setShowLoginModal(true);
                                          }
                                        }}
                                      >
                                        Get started
                                      </button>
                                    )}
                                  </UserInfo.Consumer>
                                  <Link to="/contact#contact-form-top" className="use-case-button">
                                      Learn more
                                  </Link>
                                  {useCase.beta && (
                                      <span className="beta-tag">(Beta access, by invite.)</span>
                                  )}
                              </div>
                          </div>
                      </div>
                      <div className="sticky-visual">
                          {useCase.videoUrl && (
                              <div style={{width: '100%', height: '100%', borderRadius: '24px', overflow: 'hidden', position: 'relative'}}>
                                  {useCase.thumbnail && !videosReady[useCase.videoUrl] && (
                                    <img 
                                      src={useCase.thumbnail}
                                      alt="Loading video..."
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        zIndex: 1
                                      }}
                                    />
                                  )}
                                  <ReactPlayer
                                      url={useCase.videoUrl}
                                      playing={true}
                                      loop={true}
                                      muted={true}
                                      playsInline={true}
                                      width="100%"
                                      height="100%"
                                      controls={false}
                                      onStart={() => handleVideoReady(useCase.videoUrl)}
                                      config={{
                                        vimeo: {
                                          playerOptions: {
                                            autoplay: true,
                                            muted: true,
                                            playsinline: true,
                                            background: true
                                          }
                                        }
                                      }}
                                  />
                              </div>
                          )}
                          {(useCase.imageUrl || (useCase.images && useCase.images.length > 0)) && (
                              useCase.hasWhitePanel ? (
                                  <div className="white-panel-graphic">
                                      <img 
                                          src={useCase.imageUrl || useCase.images[0]} 
                                          alt={useCase.title} 
                                          style={{ 
                                              width: '100%', 
                                              height: 'auto', 
                                              maxHeight: '100%',
                                              objectFit: 'contain',
                                          }} 
                                      />
                                  </div>
                              ) : (
                                  <div style={{
                                      width: '100%',
                                      height: '100%',
                                      backgroundImage: `url(${useCase.imageUrl || useCase.images[0]})`,
                                      backgroundSize: 'contain',
                                      backgroundPosition: 'center',
                                      backgroundRepeat: 'no-repeat',
                                  }}></div>
                              )
                          )}
                      </div>
                  </Container>
              </section>
            </div>
            </React.Fragment>
          ))}

          <section className="scroll-section cta-section text-center">
            <Container>
              <h2 className="gradient-text">Step Into the Future of Science.</h2>
              <p className="lead" style={{color: "#cce7ff"}}>
                Join our ecosystem of trusted experts and visionary creators.
              </p>
              <UserInfo.Consumer>
                {({ userInfo }) =>
                  userInfo ? (
                    <Button
                      variant="light"
                      size="lg"
                      onClick={() => navigate("/dashboard", { state: { openWizard: true } })}
                      className="cta-button"
                    >
                      Start Creating
                    </Button>
                  ) : (
                    <Button
                      variant="light"
                      size="lg"
                      onClick={() => {
                        setShowLoginModal(true);
                      }}
                      className="cta-button"
                    >
                      Get Started for Free
                    </Button>
                  )
                }
              </UserInfo.Consumer>
            </Container>
          </section>
        </div>
      </div>
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
    </>
  );
};

export default HomePage;